import { createRoot, Root } from 'react-dom/client';
import { VipChat } from './components/VipChat';
import { ChatLauncher } from './components/ChatLauncher';
import styles from './styles.css?inline';

/**
 * Theme that hosts can apply to a single embed by setting attributes:
 *   <vip-chat theme-primary-color="#7c3aed" theme-radius="14px">
 *
 * Or via DOM property for richer cases:
 *   document.querySelector('vip-chat').theme = { primaryColor: '#7c3aed', ... }
 *
 * All values are applied as CSS custom properties on the shadow-DOM mount node.
 */
export interface VipChatTheme {
  primaryColor?: string;
  accentColor?: string;
  fontFamily?: string;
  borderRadius?: string;
  headerGradient?: string;
}

class VipChatElement extends HTMLElement {
  private root: Root | null = null;
  private mount: HTMLDivElement | null = null;
  /** Set via DOM property: const el = document.querySelector('vip-chat'); el.tokenProvider = async () => ...; */
  public tokenProvider?: () => Promise<string>;
  private _theme: VipChatTheme = {};

  set theme(value: VipChatTheme) {
    this._theme = value ?? {};
    this.applyTheme();
  }
  get theme(): VipChatTheme { return this._theme; }

  static observedAttributes = [
    'server-url', 'height', 'storage-key', 'token',
    'mode',
    'intro-title', 'intro-message', 'quick-replies', 'brand-name',
    'theme-primary-color', 'theme-accent-color', 'theme-font-family',
    'theme-radius', 'theme-header-gradient',
  ];

  connectedCallback() {
    // Custom elements default to display: inline which collapses to zero
    // height regardless of inner content. Force a sensible default that
    // hosts can still override with their own CSS. Height comes from
    // applyLayout() which is mode-aware (floating mode wants no height).
    if (!this.style.display) this.style.display = 'block';
    this.applyLayout();
    const shadow = this.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = styles;
    shadow.appendChild(style);
    this.mount = document.createElement('div');
    this.mount.style.height = '100%';
    shadow.appendChild(this.mount);
    this.applyTheme();
    this.root = createRoot(this.mount);
    this.render();
  }

  /**
   * Apply the theme as CSS custom properties on the shadow-DOM root. Reads
   * both the DOM-property override and the per-attribute fallbacks.
   */
  private applyTheme(): void {
    if (!this.mount) return;
    const m = (k: string, v?: string) => {
      if (v) this.mount!.style.setProperty(k, v);
      else this.mount!.style.removeProperty(k);
    };
    const attr = (name: string) => this.getAttribute(name) ?? undefined;
    m('--vip-primary', this._theme.primaryColor ?? attr('theme-primary-color'));
    m('--vip-accent', this._theme.accentColor ?? attr('theme-accent-color'));
    m('--vip-font', this._theme.fontFamily ?? attr('theme-font-family'));
    m('--vip-radius', this._theme.borderRadius ?? attr('theme-radius'));
    m('--vip-header', this._theme.headerGradient ?? attr('theme-header-gradient'));
  }

  disconnectedCallback() {
    this.root?.unmount();
    this.root = null;
    this.mount = null;
  }

  attributeChangedCallback(name: string) {
    if (name.startsWith('theme-')) this.applyTheme();
    // mode flips the host element between taking up layout space (inline)
    // and being a zero-size anchor for a fixed-position launcher (floating).
    // Re-apply on every change so hosts that set the attribute *after*
    // append still get the right layout.
    if (name === 'mode' || name === 'height') this.applyLayout();
    if (this.root) this.render();
  }

  private applyLayout(): void {
    const isFloating = this.getAttribute('mode') === 'floating';
    if (isFloating) {
      // Don't take any layout space — the launcher uses position: fixed.
      this.style.height = '';
    } else {
      this.style.height = this.getAttribute('height') || '100%';
    }
  }

  private render() {
    const serverUrl = this.getAttribute('server-url') || '';
    const token = this.getAttribute('token') || undefined;
    const height = this.getAttribute('height') || '100%';
    const storageKey = this.getAttribute('storage-key') || 'vip-chat-session';
    const mode = (this.getAttribute('mode') === 'floating' ? 'floating' : 'inline') as 'inline' | 'floating';

    // Intro + quick-replies + brand are all optional. Empty strings count as
    // "not provided" so a host can clear them by removing the attribute.
    const introTitle = this.getAttribute('intro-title') || undefined;
    const introMessage = this.getAttribute('intro-message') || undefined;
    const quickRepliesAttr = this.getAttribute('quick-replies') || '';
    const quickReplies = quickRepliesAttr
      ? quickRepliesAttr.split('|').map(s => s.trim()).filter(Boolean)
      : undefined;
    const brandName = this.getAttribute('brand-name') || undefined;

    if (!serverUrl) {
      this.root?.render(
        <div style={{ padding: 12, fontSize: 13, color: '#6b7280' }}>
          vip-chat: missing <code>server-url</code> attribute.
        </div>,
      );
      return;
    }

    const chat = (
      <VipChat
        serverUrl={serverUrl}
        token={token}
        tokenProvider={this.tokenProvider}
        height={mode === 'floating' ? '100%' : height}
        storageKey={storageKey}
        introTitle={introTitle}
        introMessage={introMessage}
        quickReplies={quickReplies}
        brandName={brandName}
      />
    );

    if (mode === 'floating') {
      this.root?.render(<ChatLauncher>{chat}</ChatLauncher>);
    } else {
      this.root?.render(chat);
    }
  }
}

export function defineWebComponent(): void {
  if (!customElements.get('vip-chat')) {
    customElements.define('vip-chat', VipChatElement);
  }
}
